import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useRef } from 'react';
import { ObjectSchema } from 'yup';

import { EmailLeadForm } from '@components/EmailLeadCapture/constants';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { ClickTarget, TypingInput, click, typing } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';

import EmailInput from './EmailInput';
import EmailInputButton from './EmailInputButton';
import SignInOption from './SignInOption';
import validationSchema from './validationSchema';

interface EmailFormProps {
  buttonText: string;
  buttonClassName?: string;
  formClassName?: string;
  gdprClassName?: string;
  includeSignInOption?: boolean;
  inputClassName?: string;
  inputWrapperClassName?: string;
  isProcessingSubmission: boolean;
  isPro?: boolean;
  isRight?: boolean;
  isVertical?: boolean;
  onSubmit: (values: EmailLeadForm) => void;
  schema?: ObjectSchema<{ email }>;
  signInOptionClass?: string;
  theme?: Theme;
  trackingClickTarget: ClickTarget;
  trackingInputTarget: TypingInput;
}

const EmailForm = ({
  buttonText,
  buttonClassName,
  formClassName,
  includeSignInOption = false,
  inputClassName,
  inputWrapperClassName,
  isProcessingSubmission,
  isPro,
  isRight,
  isVertical,
  onSubmit,
  schema,
  signInOptionClass,
  theme = Theme.Light,
  trackingClickTarget,
  trackingInputTarget,
}: EmailFormProps) => {
  const { trackEvent } = useEventTracking();
  const leadInputStart = useRef<boolean>(false);
  const { variantId } = useBlueconicContext();

  const handleSubmit = (values: EmailLeadForm) => {
    onSubmit(values);
    trackEvent(click({ clickTarget: trackingClickTarget, clickText: buttonText }));
    captureBlueconicClickEvent(variantId);
  };

  const handleInputChange = () => {
    if (!leadInputStart.current) {
      leadInputStart.current = true;
      trackEvent(typing({ inputTarget: trackingInputTarget }));
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ email: '', gdprOptIn: false }}
      onSubmit={handleSubmit}
      validationSchema={schema ?? validationSchema()}
    >
      {({ handleSubmit }) => (
        <Form noValidate onSubmit={handleSubmit} className={clsx('w-full', formClassName)}>
          <div
            className={clsx('email-form flex w-full items-start sm:w-auto', {
              'flex-col': isVertical || isRight,
              'flex-row': !isVertical,
              'items-end': isRight,
            })}
          >
            <EmailInput
              theme={theme}
              isPro={isPro}
              isRight={isRight}
              inputWrapperClassName={inputWrapperClassName}
              inputClassName={clsx(inputClassName, {
                '!border-grayscale-charcoal': isVertical && !isPro && theme !== Theme.Dark,
              })}
              name="email"
              placeholder="Enter your email address"
              onChangeCapture={handleInputChange}
            />
            <EmailInputButton
              as="button"
              isPro={isPro}
              className={clsx(buttonClassName, 'min-w-[140px] px-24 py-[9px]', {
                'mt-8': isVertical || isRight,
                'disabled:opacity-75': isProcessingSubmission,
              })}
              isLoading={isProcessingSubmission}
            >
              {buttonText}
            </EmailInputButton>
          </div>
          {includeSignInOption && (
            <SignInOption
              className={clsx(signInOptionClass, 'mt-8 font-suisse-screen text-disclosure-sm')}
              theme={theme}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default EmailForm;
